/**
 * The Vibe Agent backend wire contract — the single source of truth for chat state.
 *
 * A transcript entry here is byte-compatible with one element of the persisted
 * `ui_transcript` (aiassist usecases/vibe_agent/transcript.py) AND with what the
 * live reducer appends for SSE events: rehydration and live streaming produce the
 * same objects, so they cannot render differently. There is deliberately no FE
 * message model layered on top.
 */

export type WireRole = 'user' | 'assistant'

// Semantic event names the current backend emits (run_manager/loop/ui_tools).
// Chat-visible ones become transcript entries; the rest drive canvas/widgets/state.
export const UPDATE_TYPES = {
  // text
  USER_MESSAGE: 'user_message',
  AI_MESSAGE: 'ai_message',
  AI_MESSAGE_CHUNK: 'ai_message_chunk',
  // chat-visible events
  TOOL_CALL_STARTED: 'tool_call_started',
  TOOL_CALL_COMPLETED: 'tool_call_completed',
  ASK_USER_QUESTION: 'ask_user_question',
  WAITING_FOR_APPROVAL: 'waiting_for_approval',
  AUTO_ANSWERED: 'auto_answered',
  // The user stopped a gate's auto-answer countdown (BE run_gates hold): the
  // gate stays open, parked. State-only — never a transcript row.
  GATE_TIMER_HELD: 'gate_timer_held',
  DRAFT_SAVED: 'draft_saved',
  ERROR: 'error',
  // run lifecycle
  STARTED: 'started',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  // Synthetic viewer event: the run behind an idle stream is dead (deploy kill /
  // crash) — resumable, unlike the hard terminals.
  INTERRUPTED: 'interrupted',
  // canvas / widgets / eval
  NODE_ADDED: 'node_added',
  EDGE_ADDED: 'edge_added',
  MULTIPLE_EDGES_ADDED: 'multiple_edges_added',
  NODE_UPDATED: 'node_updated',
  NODE_DELETED: 'node_deleted',
  MULTIPLE_NODES_DELETED: 'multiple_nodes_deleted',
  EDGE_DELETED: 'edge_deleted',
  FLOW_NAME_SUGGESTED: 'flow_name_suggested',
  BATCH_OPERATION_COMPLETED: 'batch_operation_completed',
  SYSTEM_PROMPT_CONFIGURED: 'system_prompt_configured',
  AGENT_IDENTITY_CONFIGURED: 'agent_identity_configured',
  STT_GUIDANCE_CONFIGURED: 'stt_guidance_configured',
  STT_KEYTERMS_CONFIGURED: 'stt_keyterms_configured',
  CHECKLIST_UPDATED: 'checklist_updated',
  EVALUATION_UPDATE: 'evaluation_update',
  // transport
  STREAM_GAP: 'stream_gap',
  SAVE_DRAFT_REQUESTED: 'save_draft_requested',
  HEARTBEAT: 'heartbeat',
} as const

export type UpdateType = (typeof UPDATE_TYPES)[keyof typeof UPDATE_TYPES]

export interface GateQuestionOption {
  label: string
  description?: string
  recommended?: boolean
}

export interface GateQuestion {
  header: string
  question: string
  options: GateQuestionOption[]
  multi_select?: boolean
}

// [{question: <header>, answer: [label,...]}] — the shape auto_answered carries
// and question_data.selected_answers persists.
export interface GateAnswer {
  question: string
  answer: string[]
}

// Persisted on ask_user_question entries; enriched in place when the gate resolves.
export interface WireQuestionData {
  questions?: GateQuestion[] | null
  interaction_id?: string | null
  is_answered?: boolean
  selected_answers?: GateAnswer[] | null
}

export interface TranscriptEntry {
  id: string
  role: WireRole
  message_type: 'text' | 'event'
  update_type: string
  text?: string | null
  payload?: Record<string, unknown> | null
  question_data?: WireQuestionData | null
  response_id?: string | null
  ts?: string | number | null
  /** FE-local: appended optimistically on send, not yet confirmed by the server.
   *  Survives resyncs via the pending-tail merge; adopted in place when the
   *  server's user_message event / transcript row arrives. */
  pending?: boolean
  /** FE-local: originated in this tab and not yet CONFIRMED PERSISTED by a
   *  server snapshot. Cleared only by the snapshot merge (a live user_message
   *  event clears `pending` — the dim style — but not `local`: an early
   *  gap-resync can carry a snapshot persisted before this turn). */
  local?: boolean
  /** FE-local: tail AI entry currently receiving chunks — folded in place. */
  streaming?: boolean
}

// One SSE frame's data. `workflow` is a serialized canvas snapshot — every
// canvas-mutating event carries it (mixed-deploy contract).
export interface VibeEvent {
  update_type: string
  session_id?: string | null
  response_id?: string | null
  workflow?: string | null
  payload?: Record<string, unknown> | null
}

// Mirrors the BE run_status domain (persistence.RUN_STATUS_*) exactly, plus the
// FE-only 'sending' pre-`started` state. `error` and `interrupted` are what the
// BE persists — the live reducer must use the same strings so a reloaded run
// and a live run never disagree.
export type RunStatus =
  | 'idle'
  | 'sending' // FE-only: POST in flight, no `started` yet
  | 'running'
  | 'waiting_input' // parked: gate with no recommended answer
  | 'completed'
  | 'error'
  | 'interrupted' // resumable: lease lost / park-cap expiry
  | 'cancelled'

/** A run someone could still attach to. 'sending' is FE-only, so for BE
 *  snapshot statuses it is simply never true. */
export const isRunLive = (status: RunStatus | null): boolean =>
  status === 'running' || status === 'waiting_input' || status === 'sending'

export interface PendingInteraction {
  interaction_id: string
  type: 'question' | 'approval'
  questions?: GateQuestion[] | null
  auto_answer_seconds?: number | null
}

// GET /vibe-agent/conversations/{phlo_uuid} → data
export interface ConversationSnapshot {
  session_id: string | null
  phlo_uuid: string
  run_status: RunStatus | null
  run_response_id?: string | null
  pending_interaction: PendingInteraction | null
  ui_transcript: TranscriptEntry[] | null
  workflow: string | null
  last_stream_id: string | null
  flow_name: string | null
  /** Persisted progress-tracker state ({checklist, explanation}) — absent on
   *  older backends and on sessions that never created one. */
  checklist?: { checklist?: ChecklistItem[] | null; explanation?: string | null } | null
  /** The caller's own thumbs per turn, keyed by response_id. */
  feedback?: Record<string, FeedbackVote> | null
}

export interface FeedbackVote {
  thumbs_up: boolean
  comment: string | null
}

export interface ChecklistItem {
  id?: string
  step: string
  status: 'pending' | 'in_progress' | 'completed'
}

// The armed gate the UI renders a countdown / open card for.
export interface ActiveGate {
  interactionId: string
  kind: 'question' | 'approval'
  questions: GateQuestion[]
  /** null = parked (no auto-answer timer server-side) */
  autoAnswerSeconds: number | null
}

// ---------------------------------------------------------------------------
// Typed payload readers — the ONLY place wire payload fields are cast. Every
// consumer (reducer, canvas-sync, components) goes through these; none may
// reach into event.payload with an inline `as`.

const payloadOf = (e: VibeEvent | TranscriptEntry): Record<string, unknown> => e.payload ?? {}

export const readMessage = (e: VibeEvent | TranscriptEntry): string =>
  (payloadOf(e).message as string) ?? ''

export interface GatePayload {
  interactionId: string
  questions: GateQuestion[]
  autoAnswerSeconds: number | null
}

export const readGate = (e: VibeEvent): GatePayload => ({
  interactionId: (payloadOf(e).interaction_id as string) ?? '',
  questions: (payloadOf(e).questions as GateQuestion[]) ?? [],
  autoAnswerSeconds: (payloadOf(e).auto_answer_seconds as number) ?? null,
})

export interface AutoAnswerPayload {
  interactionId: string | undefined
  interactionType: 'question' | 'approval' | undefined
  answers: GateAnswer[] | null
}

export const readAutoAnswer = (e: VibeEvent | TranscriptEntry): AutoAnswerPayload => ({
  interactionId: payloadOf(e).interaction_id as string | undefined,
  interactionType: payloadOf(e).interaction_type as 'question' | 'approval' | undefined,
  answers: (payloadOf(e).answers as GateAnswer[]) ?? null,
})

export const readChecklist = (
  e: VibeEvent,
): { checklist: ChecklistItem[] | null; explanation: string | null } => ({
  checklist: (payloadOf(e).checklist as ChecklistItem[]) ?? null,
  explanation: (payloadOf(e).explanation as string) ?? null,
})

export interface ToolCallPayload {
  toolName: string
  clientDescription: string | undefined
  toolArgs: Record<string, unknown> | undefined
}

export const readToolCall = (e: TranscriptEntry): ToolCallPayload => ({
  toolName: (payloadOf(e).tool_name as string) ?? '',
  clientDescription: payloadOf(e).client_description as string | undefined,
  toolArgs: payloadOf(e).tool_args as Record<string, unknown> | undefined,
})

export const readDraftSaveStatus = (e: VibeEvent | TranscriptEntry): string =>
  (payloadOf(e).status as string) ?? 'saved'

export const readSuggestedFlowName = (e: VibeEvent): string | undefined =>
  payloadOf(e).suggested_name as string | undefined

// completed/error/cancelled end the run. waiting_for_approval does NOT — parked
// runs keep the viewer attached for the countdown + auto_answered event.
const TERMINAL_UPDATE_TYPES: string[] = [
  UPDATE_TYPES.COMPLETED,
  UPDATE_TYPES.ERROR,
  UPDATE_TYPES.CANCELLED,
  UPDATE_TYPES.INTERRUPTED,
]

export const isTerminalUpdate = (updateType?: string): boolean =>
  !!updateType && TERMINAL_UPDATE_TYPES.includes(updateType)

// Wire events that materialize as chat rows. Everything else is canvas/widget/
// lifecycle state — the canvas snapshot carries their outcome.
export const CHAT_VISIBLE_EVENTS = new Set<string>([
  UPDATE_TYPES.TOOL_CALL_COMPLETED,
  UPDATE_TYPES.ASK_USER_QUESTION,
  // waiting_for_approval is deliberately NOT chat-visible — legacy parity:
  // the approval gate renders as buttons (with countdown), never a text row.
  UPDATE_TYPES.AUTO_ANSWERED,
  UPDATE_TYPES.DRAFT_SAVED,
  UPDATE_TYPES.ERROR,
])
